import type { BadgeTracker } from "../../aysa_api/models/tracker.types";
import type { ColorSet, Color } from "../../aysa_api/models/color.types";
import type { Image } from "../../aysa_api/models/image.types";
import type { Link } from "../../aysa_api/models/link.types";

export enum Badge_SizeFinderFit_SizeFinderBadgeStyle {
  UNSPECIFIED = 0,
  // Medium Score, usually white/grey badge
  REGULAR = 1,
  // High Score, usually green badge
  PROMINENT = 2,
}

export interface Badge {
  tracker: BadgeTracker;
  type?:
    | {
        $case: "productAttribute";
        productAttribute: Badge_ProductAttribute;
      }
    | {
        $case: "personalizedBrand";
        personalizedBrand: Badge_PersonalizedBrandPlaceholder;
      }
    | {
        $case: "sale";
        sale: Badge_Sale;
      }
    | {
        $case: "campaignSale";
        campaignSale: Badge_CampaignSale;
      }
    | {
        $case: "inBasket";
        inBasket: Badge_InBasketPlaceholder;
      }
    | {
        $case: "yourSize";
        yourSize: Badge_YourSize;
      }
    | {
        $case: "bestPrice";
        bestPrice: Badge_BestPrice;
      }
    | {
        $case: "sizeFinderFit";
        sizeFinderFit: Badge_SizeFinderFit;
      }
    | {
        $case: "upcyclingFashion";
        upcyclingFashion: Badge_UpcyclingFashion;
      }
    | {
        $case: "sustainable";
        sustainable: Badge_Sustainable;
      }
    | {
        $case: "couponIconBadge";
        couponIconBadge: Badge_CouponIconBadge;
      }
    | {
        $case: "couponDisabled";
        couponDisabled: Badge_CouponDisabled;
      }
    | {
        $case: "perfectFit";
        perfectFit: Badge_PerfectFit;
      }
    | {
        $case: "tryOn";
        tryOn: TryOn;
      }
    | {
        $case: "couponTextBadge";
        couponTextBadge: Badge_CouponTextBadge;
      };
}

// A basic badge that comes from a certain product attribute
export interface Badge_ProductAttribute {
  label: string;
  textColor?: string;
  backgroundColor?: string;
}

export interface Badge_PersonalizedBrandPlaceholder {
  brandId: number;
}

// A placeholder badge for in-basket products, this badge only serves the purpose of sorting of the badge list,
// The value has to be filtered by the client applications
export interface Badge_InBasketPlaceholder {
  productId: number;
  label: string;
}

export interface Badge_Sale {
  // The default label for the campaign sale badge should be used by the FEs in most cases.
  // There are, however cases where the FE can override this with a more specific label which is based
  // on the sale reduction of a variant.
  //
  // One case is on the category page, where when the user filters for a specific size, the FE can
  // show the label based on the filtered size instead of the default label.
  //
  // If the badge is computed for a product, it follows the following logic:
  // If all variants have a sale reduction and the relative reduction is the same, we display the exact reduction percentage.
  // If not all variants have a sale reduction or different relative reductions,
  // we show a generic message for the sale reduction.
  //
  // If the badge is computed for sizes, we will always show the exact relative reduction.
  defaultLabel: string;
  // The sale colour pair (foreground/background) the FE applies to the (non-campaign)
  // sale badge. Lets the FE stop hardcoding the static sale red (COLORS.Sale) (ASFE-14241).
  colors: ColorSet;
}

export interface Badge_CampaignSale {
  // Deprecated: use [colors]. Hex code for campaign color (may differ based on gender).
  campaignColor: string;
  // Optional. Only set if "Disable countdown" is unchecked in back office.
  countdown?: Badge_CampaignSale_Countdown;
  // The default label for the campaign sale badge should be used by the FEs in most cases.
  // There are, however cases where the FE can override this with a more specific label which is based
  // on the campaign reduction of a variant.
  //
  // One case is on the category page, where when the user filters for a specific size, the FE can
  // show the label based on the filtered size instead of the default label.
  //
  // If the badge is computed for a product, it follows the following logic:
  // If all variants have a campaign reduction and the relative reduction is the same, we display the exact reduction percentage.
  // If not all variants have a campaign reduction or different relative reductions,
  // we show a generic message for the campaign reduction.
  //
  // If the badge is computed for sizes, we will always show the exact relative reduction.
  defaultLabel: string;
  // The campaign colour pair (foreground/background) the FE applies to the badge.
  // Replaces the single [campaign_color] hex (ASFE-14241).
  colors: ColorSet;
}

export interface Badge_CampaignSale_Countdown {
  startsAt: Date;
  endsAt: Date;
}

// A badge which will be displayed on the ADP Size Selection
// for recommended sizes
export interface Badge_YourSize {
  // eg: Your Size
  label: string;
  // eg: '!'
  icon: Image;
  tooltip?: Badge_YourSize_Tooltip;
}

export interface Badge_YourSize_Tooltip {
  // eg: SizeFinder icon
  icon: Image;
  // eg: Why do we recommend this size to you?
  headline: string;
  // eg: this recommmendation is...
  body: string;
  // eg: My Sizes (/a/mysizes) | Size Finder (/a/sizefinder)
  cta: Link;
}

// A badge which will be displayed on ADP size selection of beauty products
// for the size which offers the most for the value.
export interface Badge_BestPrice {
  label: string;
}

// A badge which will be displayed on the category stream if there is a high-enought fit recommendation
// for the current product, and the size finder was enabled for the request
export interface Badge_SizeFinderFit {
  // Label, usually contains the highest score
  label: string;
  // Badge style, depending on fit score
  style: Badge_SizeFinderFit_SizeFinderBadgeStyle;
}

// A badge which will be displayed on the ADP product slider for upcycling fashion
// The icon for the badge will be handled on FE side
export interface Badge_UpcyclingFashion {
  label: string;
}

export interface Badge_Sustainable {
  label: string;
  icon: Image;
}

// a badge which requires the coupon label to be shown on it
export interface Badge_CouponIconBadge {
  // eg -25%
  label: string;
  backgroundColor: Color;
  foregroundColor: Color;
}

// a badge which only shows the text 'COUPON' without any discount information
export interface Badge_CouponTextBadge {
  // eg 'COUPON'
  label: string;
}

export interface Badge_CouponDisabled {
  // eg -25%
  label: string;
}

export interface Badge_PerfectFit {
  // eg 'Perfect Fit'
  label: string;
  // only included when the badge is displayed on the Size Selection
  tooltip?: Badge_PerfectFit_Tooltip;
}

export interface Badge_PerfectFit_Tooltip {
  // eg 'i', to be shown inside the badge on the Size Selection
  icon: Image;
  // Measuring Tape Icon
  image: Image;
  // eg 'Why do we recommend this size?'
  headline: string;
  // eg 'This recommendation is based on your body scan.'
  body: string;
  // eg {Label: 'My Body Scan', LinkTarget: Body Scan Page in Account Area}
  cta: Link;
}

export interface TryOn {
  // eg 'Try On'
  label: string;
}

// A badge which will be displayed on the ADP image gallery to highlight qualitative product information
export interface QualitativeBadge {
  // eg Excellent quality rating
  label: string;
}
